<template>
  <div class="sf-layer-border-bottom sf-pad-2">
    <div class="sf-grid sf-pad-0">
      <div class="sf-row">
        <div class="sf-col" :class="leftContainerClass">
          <div class="sf-pad-h-1" :class="leftContentClass">
            <div class="audit-message">
              <!-- TODO: we interpolate and purify message here as it's a HTML string coming from the audits_helper
                that contains  user input, and thus may potentially contain malicious javascript -->
              <span
                v-dompurify-html="message"
                :class="{ 'sf-hidden': showMore }"
                data-testid="audit-log-item__message"
              ></span>
              <template v-if="trimmed">
                <a
                  class="sf-font-xs sf-bold"
                  :class="{ 'sf-hidden': showMore }"
                  data-testid="audit-log-item__show-more"
                  @click="showMore = true"
                >
                  {{ I18n.t('audit.show_more') }}
                </a>
                <!-- TODO: we interpolate and purify fullAuditMessage here as it's a HTML string coming from the audits_helper
                  that contains user input, and thus may potentially contain malicious javascript -->
                <span
                  v-dompurify-html="fullAuditMessage"
                  :class="{ 'sf-hidden': !showMore }"
                  data-testid="audit-log-item__full-audit-message"
                ></span>
              </template>
              <span class="sf-c-text-light">
                <span
                  v-tooltip.html="formatTooltipTimestamp"
                  data-testid="audit-log-item__timestamp"
                  >{{ formatTimestamp }}</span
                >
              </span>
              <a
                v-if="isAccountsMappingRun"
                class="tw-text-sf-primary tw-ml-1 sf-pointer hover:tw-underline"
                data-testid="audit-log-item__mapping-run-details"
                @click="openMappingRunDetailsModal"
              >
                {{ I18n.t('audit.accounts_mapping_run.show_details') }}
              </a>
            </div>
          </div>
          <div
            v-if="showAuditedChanges"
            class="
              sf-font-xs
              sf-overflow-hidden
              sf-c-text-light
              sf-bg-snow-light
              sf-layer-rounded
              sf-overflow-hidden
            "
          >
            <AuditLogAttributeChange
              v-for="change in filterAttributesDropUpdatedAt(
                audit.auditedChanges
              )"
              :key="change.attribute"
              :change="change"
            />
          </div>
        </div>
        <div v-if="!useFullWidth" class="sf-col">
          <div class="sf-level">
            <div v-if="ownerLink" v-dompurify-html="ownerLink"></div>

            <div v-if="audit.restorable" class="sf-level-item">
              <SfButtonDs
                size="sm"
                :href="audit.auditRestoreLink"
                data-testid="audit-log-item__restore_link"
                @click="auditRestore"
              >
                {{ I18n.t('audit.restore') }}
              </SfButtonDs>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import AuditLogAttributeChange from './audit-log-attribute-change'
import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'
import datetimeToMoment from '@app/utils/time-converter'
import formatTimestamp from '@app/utils/time-formatter'
import { isCurrentDate } from '@app/utils/date-formatter'
import { SfButtonDs } from '@silverfin/ui-components'

export default {
  components: {
    AuditLogAttributeChange,
    SfButtonDs,
  },
  props: {
    fullAuditMessage: {
      type: String,
      default: undefined,
    },
    id: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      required: true,
    },
    trimmed: {
      type: Boolean,
    },
    ownerLink: {
      type: String,
      default: null,
    },
    ownerName: {
      type: String,
      default: null,
    },
    audit: {
      type: Object,
      required: true,
      validator: (value) => 'createdAt' in value,
    },
  },
  data: function () {
    return {
      showMore: false,
    }
  },
  computed: {
    leftContainerClass() {
      return this.useFullWidth ? '' : 'sf-col sf-xs-8'
    },
    leftContentClass() {
      return this.useFullWidth ? '' : 'sf-pad-h-1 sf-level sf-level-between'
    },
    useFullWidth() {
      return this.audit.auditableType === 'Adjustment'
    },
    formatTimestamp() {
      return formatTimestamp(this.audit.createdAt, {
        shouldUseLocalTimezone: true,
        shouldUseRelativeFormatOnCurrentDate: true,
      })
    },
    formatTooltipTimestamp() {
      const originalMoment = datetimeToMoment(this.audit.createdAt, true)

      if (!isCurrentDate(originalMoment)) return undefined

      return formatTimestamp(this.audit.createdAt, {
        shouldUseLocalTimezone: true,
        shouldUseRelativeFormatOnCurrentDate: false,
      })
    },
    showAuditedChanges() {
      return (
        this.audit.auditableType === 'Company' && this.audit.action === 'update'
      )
    },
    isAccountsMappingRun() {
      return (
        this.audit.auditableType === 'Company' &&
        this.audit.action === 'accounts_mapping_run'
      )
    },
    accountsMappingRunDiffRoute() {
      return Silverfin.Routes.accounts_mapping_run_diff_ledger_audit_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        Silverfin.currentLedgerId(),
        this.id
      )
    },
  },
  mounted() {
    this.initTooltips()
  },
  updated() {
    this.initTooltips()
  },
  methods: {
    initTooltips() {
      this.$nextTick(() => {
        $(this.$el)
          .find('[data-toggle="tooltip"]')
          .each((_idx, element) => {
            $(element).trigger('tooltipsRendered', element.parentElement)
          })
      })
    },
    async auditRestore(e) {
      e.preventDefault()

      try {
        await SFetch.postFormRedirect({
          url: this.audit.auditRestoreLink,
          authenticityToken: Silverfin.csrfToken(),
        })
      } catch (e) {
        SfLog.warn('Failed to restore audit', {
          extra: {
            error: e,
          },
        })
      }
    },
    filterAttributesDropUpdatedAt(auditedChanges) {
      // this is an internal attribute that was wrongly logged until December 2023, so filter old entries for users
      return auditedChanges.filter(
        (c) => c.originalAttribute !== 'attributes_drop_updated_at'
      )
    },
    openMappingRunDetailsModal(e) {
      window.modalCitizen.openXhrModal(
        e,
        'accounts-mapping-run-diff',
        this.accountsMappingRunDiffRoute,
        {}
      )
    },
  },
}
</script>
